import React from "react";
import { useSelector } from "react-redux";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Text } from "@performyard/py-components";

import type { GoalSuggestionState } from "../types";

import { getRequireGoalCategory } from "../../../selectors/settings";

import { formatTargetWithUnits } from "../utils";

import { useAskPerformYardStore } from "../AskPerformYardStore";

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr + "T00:00:00");
	return Number.isNaN(date.getTime())
		? dateStr
		: date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

type GoalPreviewCardProps = {
	suggestionState: GoalSuggestionState;
	onCreateGoal: () => void;
	onDeleteGoal: () => void;
	isCreating?: boolean;
};

export const GoalPreviewCard: React.FC<GoalPreviewCardProps> = ({
	suggestionState,
	onCreateGoal,
	onDeleteGoal,
	isCreating,
}) => {
	const hasGoalCategories = useAskPerformYardStore((state) => state.hasGoalCategories);
	const requireCategory = useSelector(getRequireGoalCategory);
	const { suggestion, status } = suggestionState;

	if (status === "deleted") {
		return null;
	}

	const isCreated = status === "created";

	return (
		<div className="flex min-w-0 max-w-full flex-col gap-0">
			{/* Goal Card */}
			<div className="box-border flex min-w-0 max-w-full flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-1 p-3">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					{suggestion.name}
				</Text>
				<div className="flex flex-col gap-1">
					<Text size="caption" color="neutral-9">
						Target: {formatTargetWithUnits(suggestion.target, suggestion.units)}
					</Text>
					<Text size="caption" color="neutral-9">
						Assignee: {suggestion.assigneeName}
					</Text>
					<Text size="caption" color="neutral-9">
						Start Date: {formatDate(suggestion.startDate)}
					</Text>
					<Text size="caption" color="neutral-9">
						End Date: {formatDate(suggestion.endDate)}
					</Text>
					{(requireCategory || suggestion.categories.length > 0) && hasGoalCategories && (
						<Text size="caption" color="neutral-9">
							Category: {suggestion.categories.map((c) => c.name).join(", ")}
						</Text>
					)}
				</div>
			</div>

			{/* Action Bar */}
			<div className="flex min-w-0 max-w-full items-center justify-between">
				<div className="shrink-0">
					{!isCreated && (
						<ButtonIcon
							icon={faTrashCan}
							onClick={onDeleteGoal}
							variant="standard"
							color="neutral"
							shade={5}
							size={16}
							aria-label="Delete goal suggestion"
						/>
					)}
				</div>
				<div className="shrink-0">
					{isCreated ? (
						<ButtonStandard
							variant="text"
							color="green"
							size="medium"
							iconLeft={faCircleCheck}
							onClick={() => {
								if (suggestionState.createdGoalId) {
									window.open(`/goal/${suggestionState.createdGoalId}/`, "_blank");
								}
							}}
							dangerouslySetClassName="!w-auto"
						>
							Goal Created
						</ButtonStandard>
					) : (
						<ButtonStandard
							variant="text"
							color="primary"
							size="medium"
							iconLeft={faCirclePlus}
							onClick={onCreateGoal}
							disabled={isCreating}
							dangerouslySetClassName="!w-auto"
						>
							{isCreating ? "Creating..." : "Create Goal"}
						</ButtonStandard>
					)}
				</div>
			</div>
		</div>
	);
};

export default GoalPreviewCard;
